import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import classNames from 'classnames';
import NextImage from '$/components/NextImage';
import IconFont from '$/components/IconFont';
import { getMyIpProfileDisplay } from '$/domains/myIp/profileDisplay';
import { message } from '$/components/Message';
import { useMyIpAssetList } from '$/hooks/myIp/useMyIpAssetList';
import type { MyIpItem } from '$/domains/myIp/types';
import type { MyIpAssetReturnState, MyIpAssetDetailViewState } from '$/domains/myIp/returnState';
import { useLanguageStore } from '$/store/languageStore';
import { useCardAction } from '../useCardAction';
import { useMyIpCopy } from '../useMyIpCopy';
import AssetDetailContent from './AssetDetailContent';
import { useMyIpAssetDetail } from './useMyIpAssetDetail';
import styles from './index.module.scss';

export interface MyIpAssetPanelProps {
  scopeKey: string;
  enabled: boolean;
  assetType: 'personal' | 'official';
  getDetailHref: (item: MyIpItem) => string;
  coverUrls?: Record<string, string>;
  onCreate?: () => Promise<void>;
  createDisabled?: boolean;
  creationFailed?: boolean;
  initialState?: MyIpAssetReturnState;
  onViewSettings?: (item: MyIpItem, state: Omit<MyIpAssetReturnState, 'kind' | 'host' | 'assetType'>) => void;
  onTrackCardShow?: (item: MyIpItem, positionId: number, keyword: string) => void;
  onTrackCardClick?: (item: MyIpItem, positionId: number, keyword: string) => void;
  onTrackIpDetailClick?: (item: MyIpItem) => void;
  onApplyToCanvas?: (item: MyIpItem) => Promise<void>;
  applyToCanvasDisabled?: boolean;
  /** 资产库选择器将 IP 添加到输入框角色预览区。 */
  onAdd?: (items: MyIpItem[]) => void | Promise<void>;
  /** 本次最多可添加的角色数，角色与图片共享输入额度。 */
  maxSelectableCount?: number;
  /** 单张与批量主按钮文案，由资产库宿主统一传入“添加”。 */
  addLabel?: string;
  /** 隐藏详情「更多素材」页签；仅爆款复刻商品槽选 IP 三视图时开启。 */
  hideMaterials?: boolean;
  /** 隐藏右栏详情，仅保留卡片与添加/批量；商品槽选 IP 三视图时开启。 */
  hideIpDetail?: boolean;
}

/** 游客通过宿主登录；个人资产面板只展示个人 IP，官方资产由独立分类承载。 */
export default function MyIpAssetPanel({
  scopeKey,
  enabled,
  assetType,
  getDetailHref,
  coverUrls = {},
  onCreate,
  createDisabled = false,
  creationFailed = false,
  initialState,
  onViewSettings,
  onTrackCardShow,
  onTrackCardClick,
  onTrackIpDetailClick,
  onApplyToCanvas,
  applyToCanvasDisabled = false,
  onAdd,
  maxSelectableCount,
  addLabel,
  hideMaterials = false,
  hideIpDetail = false,
}: MyIpAssetPanelProps) {
  const { asset: copy, detail: detailCopy } = useMyIpCopy();
  const languageStore = useLanguageStore((state) => state.languageStore);
  const batchProcessingText = languageStore.smartcatTranslationText((value) => value.batched_processing);
  const quitBatchText = languageStore.smartcatTranslationText((value) => value.asset_quit_batch);
  const resolvedAddLabel = addLabel
    || languageStore.smartcatTranslationText((value) => value.RoboNeo_Web_studio_tool_add);
  /** 请求 hook 保留最新回调，迟到的网络提示也使用当前语言，不因换语言重发查询。 */
  const notifyNetworkError = () => message.error(copy.networkError);
  const keyword = '';
  const selectionContext = JSON.stringify([scopeKey, enabled, assetType]);
  const [selection, setSelection] = useState({ context: selectionContext, id: initialState?.selectedId ?? '' });
  const selectedId = selection.context === selectionContext ? selection.id : '';
  const restoration = useRef(initialState);
  const detailRestoration = useRef(initialState);
  const mainRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLUListElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const detailRef = useRef<HTMLDivElement>(null);
  const state = useMyIpAssetList(scopeKey, enabled, assetType, keyword, notifyNetworkError);
  const listAssetType = state.assetType === 'official' ? 'official' : assetType;
  const selected = enabled ? state.list.find((item) => item.ipId === selectedId) : undefined;
  const detailAssetType = selected?.assetType === 'personal' || selected?.assetType === 'official' ? selected.assetType : listAssetType;
  const detail = useMyIpAssetDetail(scopeKey, enabled, detailAssetType, selected?.ipId);
  let detailErrorMessage = copy.loadError;
  if (detail.status === 'forbidden') detailErrorMessage = detailCopy.forbidden;
  if (detail.status === 'unavailable') detailErrorMessage = detailCopy.notFound;
  const listContext = JSON.stringify([scopeKey, assetType, listAssetType, keyword]);
  const [unavailable, setUnavailable] = useState<{ context: string; ids: string[]; }>({ context: listContext, ids: [] });
  const unavailableIds = unavailable.context === listContext ? unavailable.ids : [];
  const visibleList = state.list.filter((item) => item.assetType !== 'official' || (!unavailableIds.includes(item.ipId)
    && !(detail.status === 'unavailable' && item.ipId === selectedId)));
  const create = useCardAction(onCreate);
  const [failedCoverUrls, setFailedCoverUrls] = useState<string[]>([]);
  const reportedCardShowKeysRef = useRef<Set<string>>(new Set());
  const [batchMode, setBatchMode] = useState(false);
  const [batchSelectedIds, setBatchSelectedIds] = useState<Set<string>>(() => new Set());
  const [adding, setAdding] = useState(false);
  const selectableLimit = maxSelectableCount == null
    ? Number.POSITIVE_INFINITY
    : Math.max(0, Math.floor(maxSelectableCount));
  /** 爆款复刻商品槽：对齐 ChooseIpModal，卡片点多选 + 底部已选栏 + 确认，无右栏详情。 */
  const chooseIpSelection = hideIpDetail && Boolean(onAdd);
  const multiSelectActive = batchMode || chooseIpSelection;

  /** 列表上下文变化后允许同一角色在新的搜索结果集中再次曝光。 */
  useEffect(() => {
    reportedCardShowKeysRef.current.clear();
  }, [listContext]);

  /** 角色 IP 卡片进入当前结果集后按卡片和位置去重上报曝光。 */
  useEffect(() => {
    visibleList.forEach((item, positionId) => {
      const reportKey = `${listContext}:${item.assetType}:${item.ipId}:${positionId}`;
      if (reportedCardShowKeysRef.current.has(reportKey)) return;
      reportedCardShowKeysRef.current.add(reportKey);
      onTrackCardShow?.(item, positionId, keyword);
    });
  }, [keyword, listContext, onTrackCardShow, visibleList]);

  /** 返回时重取实际候选来源的分页；个人列表不再为空时取消旧官方恢复目标。 */
  useEffect(() => {
    const target = restoration.current;
    if (!target || !enabled || state.status !== 'success' || state.loadingMore || state.error) return;
    if (selection.context !== selectionContext || (target.listAssetType ?? target.assetType) !== listAssetType) {
      restoration.current = undefined;
      detailRestoration.current = undefined;
      setSelection({ context: selectionContext, id: '' });
      return;
    }
    if ((state.loadedPages || 1) < target.page && state.hasMore) { state.loadMore(); return; }
    if (mainRef.current) mainRef.current.scrollTop = target.scrollTop;
    if (panelRef.current) panelRef.current.scrollTop = target.panelScrollTop;
    restoration.current = undefined;
  }, [enabled, state, listAssetType, selection.context, selectionContext]);

  /** 接近底部时自动追加角色；分页失败由用户重试，避免错误时循环请求。 */
  const loadNextPageIfNeeded = useCallback(() => {
    const main = mainRef.current;
    if (!enabled || !main || main.clientHeight <= 0 || restoration.current
      || state.status !== 'success' || !state.hasMore || state.loadingMore || state.error) return;
    if (main.scrollHeight - main.scrollTop - main.clientHeight < 400) state.loadMore();
  }, [enabled, state]);

  /** 首屏不足一屏或面板尺寸改变时继续补齐卡片，无需“展示更多”按钮。 */
  useEffect(() => {
    loadNextPageIfNeeded();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(loadNextPageIfNeeded);
    if (mainRef.current) observer.observe(mainRef.current);
    if (gridRef.current) observer.observe(gridRef.current);
    return () => observer.disconnect();
  }, [loadNextPageIfNeeded]);

  /** 右栏内容异步加载完才恢复滚动，避免在加载占位上被浏览器截断为零。 */
  useEffect(() => {
    const target = detailRestoration.current;
    if (!target || !enabled || detail.status !== 'success' || detail.item?.ipId !== target.selectedId) return;
    if (detailRef.current) detailRef.current.scrollTop = target.detailScrollTop;
    detailRestoration.current = undefined;
  }, [enabled, detail]);

  /** 详情明确不存在或分类不匹配时隐藏官方卡片；普通网络失败不推断下架。 */
  useEffect(() => {
    if (selected?.assetType !== 'official' || !selectedId || !['unavailable', 'success'].includes(detail.status)) return;
    setUnavailable((previous) => {
      const ids = previous.context === listContext ? previous.ids : [];
      const next = detail.status === 'unavailable' ? Array.from(new Set([...ids, selectedId])) : ids.filter((id) => id !== selectedId);
      if (previous.context === listContext && next.length === ids.length) return previous;
      return { context: listContext, ids: next };
    });
  }, [selected?.assetType, selectedId, listContext, detail.status]);

  /** 用户主动换卡或关闭时取消原详情滚动恢复，不让迟到的恢复覆盖当前浏览位置。 */
  const selectIp = useCallback((ipId: string) => {
    restoration.current = undefined;
    detailRestoration.current = undefined;
    setSelection({ context: selectionContext, id: ipId });
    if (detailRef.current) detailRef.current.scrollTop = 0;
  }, [selectionContext]);

  /** 批量或 ChooseIp 式多选切换勾选；否则非 hideIpDetail 时选中卡片打开详情。 */
  const handleIpCardClick = useCallback((item: MyIpItem, positionId: number) => {
    onTrackCardClick?.(item, positionId, keyword);
    if (multiSelectActive) {
      setBatchSelectedIds((previous) => {
        const next = new Set(previous);
        if (next.has(item.ipId)) next.delete(item.ipId);
        else if (next.size < selectableLimit) next.add(item.ipId);
        else if (Number.isFinite(selectableLimit)) {
          message.error(`最多选择${selectableLimit}个IP`);
        }
        return next;
      });
      return;
    }
    if (!hideIpDetail) selectIp(item.ipId);
  }, [hideIpDetail, keyword, multiSelectActive, onTrackCardClick, selectableLimit, selectIp]);

  /** 从底部已选列表移除单个角色（ChooseIp 式交互）。 */
  const handleRemoveSelectedIp = useCallback((ipId: string) => {
    setBatchSelectedIds((previous) => {
      if (!previous.has(ipId)) return previous;
      const next = new Set(previous);
      next.delete(ipId);
      return next;
    });
  }, []);

  /** 切换角色 IP 批量选择模式；退出时丢弃未确认的选择。 */
  const toggleBatchMode = useCallback(() => {
    setBatchSelectedIds(new Set());
    setBatchMode((previous) => !previous);
  }, []);

  /** 将单张或批量角色交给输入框宿主，成功后退出选择态。 */
  const handleAdd = useCallback(async (items: MyIpItem[]) => {
    if (!onAdd || adding || items.length === 0) return;
    setAdding(true);
    try {
      await onAdd(items);
      setBatchSelectedIds(new Set());
      setBatchMode(false);
    } finally {
      setAdding(false);
    }
  }, [adding, onAdd]);

  const batchSelectedItems = useMemo(
    () => visibleList.filter((item) => batchSelectedIds.has(item.ipId)),
    [batchSelectedIds, visibleList],
  );
  const batchSelectedCountText = languageStore.smartcatTranslationText((value) => value['teams_2.0_assets_selected_num'])
    .replace(/\{[^}]+\}/, String(batchSelectedItems.length));
  const batchUnselectText = languageStore.smartcatTranslationText((value) => value.asset_unselect);

  /** 宿主页签与候选来源分别记录，查看官方兜底后仍恢复原个人页签及官方分页。 */
  const rememberView = (view: MyIpAssetDetailViewState) => {
    if (!detail.item) return;
    onViewSettings?.(detail.item, {
      input: '',
      keyword,
      selectedId,
      ...(listAssetType !== assetType ? { listAssetType } : {}),
      page: state.loadedPages || 1,
      scrollTop: mainRef.current?.scrollTop ?? 0,
      panelScrollTop: panelRef.current?.scrollTop ?? 0,
      detailScrollTop: detailRef.current?.scrollTop ?? 0,
      ...(view.tab !== 'settings' || view.mode !== 'main' || view.imageIndex !== 0 ? { detailView: view } : {}),
    });
  };

  /** 签名媒体加载失败后使用明确占位，不能把资产标识拼成图片地址。 */
  const cover = (item: MyIpItem, className: string) => {
    const url = coverUrls[item.ipId] ?? item.coverUrl;
    return url && !failedCoverUrls.includes(url)
      ? <NextImage className={className} src={url} alt={item.name} crossOrigin="anonymous" onError={() => setFailedCoverUrls((previous) => [...previous, url])} />
      : <div className={className} role="img" aria-label={copy.coverUnavailable} />;
  };

  let selectionTotalHint = 0;
  if (Number.isFinite(selectableLimit)) {
    selectionTotalHint = selectableLimit;
  } else if (state.status === 'success') {
    selectionTotalHint = visibleList.length;
  }

  if (!enabled && assetType === 'official') return <p className={styles.status}>{copy.loginRequired}</p>;
  const creationDisabled = !onCreate || createDisabled || create.pending;
  return (
    <div ref={panelRef} className={classNames(styles.panel, chooseIpSelection && styles.chooseIpPanel)} data-ip-asset-panel>
      <div ref={mainRef} className={styles.main} onScroll={loadNextPageIfNeeded}>
        <div className={styles.toolbar}>
          <h2>{copy.title}</h2>
          {chooseIpSelection && (
            <span className={styles.selectionCount}>
              {`${batchSelectedItems.length}/${selectionTotalHint}`}
            </span>
          )}
          {onAdd && !chooseIpSelection && (
            <button type="button" className={styles.batchModeButton} aria-pressed={batchMode} onClick={toggleBatchMode}>
              <IconFont name={batchMode ? 'arrowRightBrackets' : 'multipleChoiceBold'} fontSize={14} />
              {batchMode ? quitBatchText : batchProcessingText}
            </button>
          )}
        </div>
        {enabled && state.error != null && (
        <p role="alert">
          {copy.loadError}
          {' '}
          <button type="button" onClick={state.status === 'error' ? state.refresh : state.loadMore}>{copy.retry}</button>
        </p>
        )}
        {enabled && state.status === 'loading' && <p role="status" aria-busy="true">{copy.loading}</p>}
        {(create.failed || creationFailed) && <p role="alert">{copy.actionError}</p>}
        <ul ref={gridRef} className={styles.grid}>
          {assetType === 'personal' && onCreate && (
            <li>
              <button type="button" className={styles.createCard} onClick={create.run} disabled={creationDisabled || batchMode}>
                <IconFont name="plusBold" fontSize={20} />
                <strong>{copy.create}</strong>
                <span>{copy.createDescription}</span>
              </button>
            </li>
          )}
          {enabled && visibleList.map((item, positionId) => (
            <li key={item.ipId} className={styles.cardItem}>
              <button
                type="button"
                className={styles.card}
                aria-pressed={multiSelectActive ? batchSelectedIds.has(item.ipId) : selected?.ipId === item.ipId}
                onClick={() => handleIpCardClick(item, positionId)}
              >
                <span className={styles.coverWrap}>
                  {cover(item, styles.cover)}
                  {multiSelectActive && (
                    <span className={styles.selectionCheckbox} aria-hidden>
                      {batchSelectedIds.has(item.ipId) && <IconFont name="checkBoxMark" fontSize={10} color="#fff" />}
                    </span>
                  )}
                  {chooseIpSelection && batchSelectedIds.has(item.ipId) && (
                    <span className={styles.selectedTag}>
                      <IconFont name="checkV2Bold" fontSize={10} />
                      <span>{languageStore.smartcatTranslationText((value) => value.common_label_selected)}</span>
                    </span>
                  )}
                </span>
                <div className={styles.cardCaption}>
                  <strong title={item.name}>{item.name}</strong>
                  <span>{getMyIpProfileDisplay(item.profile ?? {}).identity || item.summary}</span>
                </div>
              </button>
              {!multiSelectActive && onAdd && (
                <button
                  type="button"
                  className={styles.addButton}
                  disabled={adding || selectableLimit < 1}
                  onClick={() => handleAdd([item])}
                >
                  {resolvedAddLabel}
                </button>
              )}
            </li>
          ))}
        </ul>
        {!enabled && <p className={styles.status}>{copy.loginRequired}</p>}
        {/* 角色 IP 无数据时保持列表区域为空，避免资产库展示通用空数据文案。 */}
        {enabled && state.loadingMore && <p role="status" aria-busy="true">{copy.loading}</p>}
        {batchMode && onAdd && !chooseIpSelection && (
          <div className={styles.batchFooter} role="toolbar">
            <div className={styles.batchSelectionContent}>
              <span className={styles.batchSelectionCount}>{batchSelectedCountText}</span>
              <div className={styles.batchSelectedList}>
                {batchSelectedItems.map((item) => (
                  <div key={item.ipId} className={styles.batchSelectedItem}>
                    {cover(item, styles.batchSelectedAvatar)}
                    <span className={styles.batchSelectedName} title={item.name}>{item.name}</span>
                    <button
                      type="button"
                      className={styles.batchSelectedRemove}
                      aria-label={`${batchUnselectText} ${item.name}`}
                      disabled={adding}
                      onClick={() => handleRemoveSelectedIp(item.ipId)}
                    >
                      <IconFont name="crossFill" fontSize={12} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
            <button type="button" disabled={adding || batchSelectedItems.length === 0} onClick={() => handleAdd(batchSelectedItems)}>
              {resolvedAddLabel}
            </button>
          </div>
        )}
      </div>
      {chooseIpSelection && onAdd && (
        <>
          <section className={classNames(styles.chooseIpSelectedPanel, !batchSelectedItems.length && styles.chooseIpSelectedPanelEmpty)}>
            {batchSelectedItems.length > 0 && (
              <>
                <div className={styles.chooseIpSelectedTitle}>
                  {languageStore.smartcatTranslationText((value) => value.creation_cast_selected_characters).replace(/\{count\}/g, String(batchSelectedItems.length))}
                </div>
                <div className={styles.chooseIpSelectedList}>
                  {batchSelectedItems.map((item) => (
                    <div key={item.ipId} className={styles.chooseIpSelectedItem}>
                      {cover(item, styles.chooseIpSelectedAvatar)}
                      <span className={styles.chooseIpSelectedName} title={item.name}>{item.name}</span>
                      <button
                        type="button"
                        className={styles.chooseIpRemoveButton}
                        aria-label={`${languageStore.smartcatTranslationText((value) => value.asset_unselect)} ${item.name}`}
                        disabled={adding}
                        onClick={() => handleRemoveSelectedIp(item.ipId)}
                      >
                        <IconFont name="crossFill" fontSize={12} />
                      </button>
                    </div>
                  ))}
                </div>
              </>
            )}
          </section>
          <footer className={classNames(styles.chooseIpFooter, !batchSelectedItems.length && styles.chooseIpFooterEmpty)}>
            <button
              type="button"
              className={styles.chooseIpConfirmButton}
              disabled={adding || batchSelectedItems.length === 0 || selectableLimit < 1}
              onClick={() => handleAdd(batchSelectedItems)}
            >
              {resolvedAddLabel}
            </button>
          </footer>
        </>
      )}
      {selected && !multiSelectActive && !hideIpDetail && (
        <aside className={styles.detail} aria-label={copy.detail}>
          {detail.status === 'loading' && <p role="status" aria-busy="true">{copy.loading}</p>}
          {['error', 'forbidden', 'unavailable'].includes(detail.status) && (
            <div role="alert" className={styles.detailError}>
              <p>{detailErrorMessage}</p>
              {detail.status === 'forbidden' && <p>{detailCopy.privateDescription}</p>}
              <button type="button" onClick={detail.refresh}>{copy.retry}</button>
            </div>
          )}
          {detail.status === 'success' && detail.item && <AssetDetailContent scrollRef={detailRef} key={detail.contextKey} item={detail.item} coverUrl={coverUrls[detail.item.ipId]} detailHref={getDetailHref(detail.item)} initialView={detailRestoration.current?.selectedId === detail.item.ipId ? detailRestoration.current.detailView : undefined} onViewSettings={rememberView} onIpDetailClick={onTrackIpDetailClick} onApplyToCanvas={onApplyToCanvas} applyToCanvasDisabled={applyToCanvasDisabled} actionLabel={onAdd ? resolvedAddLabel : undefined} hideMaterials={hideMaterials} />}
        </aside>
      )}
    </div>
  );
}
